import Image from "next/image";

import { faqs, howItWorks } from "./helpers";
import { routes } from "@/global/routes";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

import BannerImage from "public/images/msg/NYK2526.jpg";
import FaviconImage from "public/images/msg/msglogo.png";
import BackgroundImage from "public/images/msg/madison-square-garden.jpg";
import SeatMap3dPreview from "public/images/rangers/rangers_3d_venue.png";

const logoImage =
  "https://ss-leagues.s3.amazonaws.com/NHL/rangers-knicks-logo.png?width=128&height=78";

const config: TeamConfig = {
  teamSchema: "msg",
  browserTitle: "MSG Flex",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Madison Square Garden",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-00045-knicks-sec",
    mapId: "main",
    dynamicSeatMapIds: [
      {
        category: "Knicks",
        venueId: "nam-us-00045-knicks-sec",
        mapId: "main",
      },
      {
        category: "Rangers",
        venueId: "nam-us-00045-rangers-sec",
        mapId: "main",
      },
    ],
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.purchaseSeats,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: true,
  isCrowdHandlerEnabled: false,
  inventoryType: inventoryTypeUIDs.sectionLevel,

  // purchase seat configs
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,

    showFrontendTotalAmount: false,
    showFrontendServiceFee: false,
  },

  authConfigs: {
    isLoginRequired: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
    checkoutTimerEnabled: false,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  isSaveCardAllowed: true,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // call refresh inventory when selecting games
  refreshInventoryWhenEventClick: false,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "https://flex.attendevents.com/msg_flex",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/msg/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  // images
  images: {
    logoImage,
    logoNameImage: "",
    faviconImage: FaviconImage.src,
    findGamesBannerImage: BannerImage.src,
    authBackgroundImage: BackgroundImage.src,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
    checkoutStatusPageImage: BackgroundImage.src,
  },

  colors: {
    primary: "#005189",
  },

  howItWorks,
  faqs,

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },
    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },
    bottomBar: {
      showGameSelectionProgress: false,
    },
  },

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarAditionalText: " to continue!",
      progressBarSuccessText:
        "You're good to go! Add more events or select Continue to view seating options.",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your MSG tickets are confirmed! Tickets will be available in your MSG Account Manager shortly.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    gameFilters: {
      category: {
        title: "Teams",
        description: "Teams",
      },
    },

    authPage: {
      loginPage: {
        loginCard: (
          <>
            <Image
              width={1133}
              height={120}
              alt="MSG Flex Plans"
              src={BannerImage.src}
              style={{
                width: "100%",
                height: "100%",
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            />
          </>
        ),
        loginCardDescription:
          "Please log in to your MSG Ticketmaster account to get started.",
        loginCardButtonText: "Get Started",
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
